import { useMemo } from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { Checkbox } from "@/components/_atoms/checkbox";
import { Input } from "@/components/_atoms/input";
import { Label } from "@/components/_atoms/label";
import { Switch } from "@/components/_atoms/switch";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { DATA_REQUEST_FREE_TEXT_QUESTION_MAX_LENGTH, DATA_REQUEST_MAX_FREE_TEXT_QUESTIONS } from "@ark/shared";

import { selectedEntityTypes, useCreateDataRequestStore } from "../create-data-request-state";
import { getAvailableFields } from "../data-request-fields";

export function FieldSelectionStep() {
  const { t } = useDomainTranslation("data-request");

  const advancedFieldToggle = useCreateDataRequestStore((s) => s.advancedFieldToggle);
  const setAdvancedFieldToggle = useCreateDataRequestStore((s) => s.setAdvancedFieldToggle);
  const fieldSelection = useCreateDataRequestStore((s) => s.fieldSelection);
  const toggleField = useCreateDataRequestStore((s) => s.toggleField);
  const selectedEntities = useCreateDataRequestStore((s) => s.selectedEntities);
  const enableDocuments = useCreateDataRequestStore((s) => s.enableDocuments);
  const setEnableDocuments = useCreateDataRequestStore((s) => s.setEnableDocuments);
  const freeTextQuestions = useCreateDataRequestStore((s) => s.freeTextQuestions);
  const addFreeTextQuestion = useCreateDataRequestStore((s) => s.addFreeTextQuestion);
  const setFreeTextQuestion = useCreateDataRequestStore((s) => s.setFreeTextQuestion);
  const removeFreeTextQuestion = useCreateDataRequestStore((s) => s.removeFreeTextQuestion);

  const selectedEntityTypeList = useMemo(() => selectedEntityTypes(selectedEntities), [selectedEntities]);

  const availableFieldList = useMemo(
    () => getAvailableFields(selectedEntityTypeList),
    [selectedEntityTypeList],
  );

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-6 py-6">
      <div className="flex items-center justify-between">
        <Label>{t("create.stepFields")}</Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="advanced-field-toggle"
          checked={advancedFieldToggle}
          onCheckedChange={setAdvancedFieldToggle}
          size="sm"
          aria-label={t("create.advancedFieldSelection")}
        />
        <label htmlFor="advanced-field-toggle" className="text-sm text-muted-foreground">{t("create.advancedFieldSelection")}</label>
      </div>

      <div className="flex flex-col gap-1 rounded-md border p-3">
        {availableFieldList.map((field) => {
          const isAllFields = !advancedFieldToggle;
          const isChecked = isAllFields || (fieldSelection?.includes(field.key) ?? false);
          return (
            <div
              key={field.key}
              className={`flex items-center gap-2 py-1 ${isAllFields ? "opacity-60" : ""}`}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => toggleField(field.key)}
                disabled={isAllFields}
              />
              <span className="text-sm">{t(field.labelKey)}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 rounded-md border border-dashed p-3">
        <Checkbox
          id="request-documents"
          checked={enableDocuments}
          onCheckedChange={(checked) => setEnableDocuments(checked === true)}
        />
        <label htmlFor="request-documents" className="flex flex-col gap-0.5">
          <span className="text-sm">{t("create.fields.documents")}</span>
          <span className="text-xs text-muted-foreground">{t("create.documentsDescription")}</span>
        </label>
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-dashed p-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm">{t("create.freeTextQuestionsLabel")}</span>
          <span className="text-xs text-muted-foreground">{t("create.freeTextQuestionsDescription")}</span>
        </div>

        {freeTextQuestions.map((question, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              value={question}
              onChange={(event) => setFreeTextQuestion(index, event.target.value)}
              placeholder={t("create.freeTextQuestionPlaceholder")}
              maxLength={DATA_REQUEST_FREE_TEXT_QUESTION_MAX_LENGTH}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeFreeTextQuestion(index)}
              aria-label={t("create.removeFreeTextQuestion")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={addFreeTextQuestion}
          disabled={freeTextQuestions.length >= DATA_REQUEST_MAX_FREE_TEXT_QUESTIONS}
        >
          <Plus className="mr-1 h-3 w-3" />
          {t("create.addFreeTextQuestion")}
        </Button>
      </div>
    </div>
  );
}
